/**
 * The index's ASK CODECLEAR (AI) half: the Chat nav row and the conversations
 * section. CHAT_ENABLED gates only the section (Leo 2026-10-01); the nav row
 * always renders.
 */
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  useChatThreadsListQuery,
  useDeleteChatThreadMutation,
} from "@/queries/queries";
import { useChatSidebar } from "@/components/chat/useChatSidebar";
import ChatRowDeleteConfirm from "@/components/chat/ChatRowDeleteConfirm";
import { formatRelativeChatTime } from "@/lib/formatRelativeChatTime";
import type { ChatThreadListItem } from "@/pages/Staff/Chat/types";
import {
  IndexGroupRule,
  groupMetaClass,
  groupTitleClass,
  navRowClass,
} from "./TrackerSidebarCases";

// ---------------------------------------------------------------------------
// Conversations list: ``useChatThreadsListQuery``, limit=25 for v1. Cursor
// pagination ("load more") is v1.1.
// ---------------------------------------------------------------------------

const CONVERSATIONS_PAGE_SIZE = 25;

/**
 * Row label. The BE computes ``title`` at read time from the first user
 * message (``"New chat"`` when empty), so it should never be missing, but a
 * whitespace-only first message truncates to "", hence the fallback.
 */
const resolveTitle = (thread: ChatThreadListItem): string => {
  const t = (thread.title ?? "").trim();
  if (t) return t;
  return "Untitled conversation";
};

// ---------------------------------------------------------------------------
// Conversations list: loading / empty / error are first-class states. A 403
// (chat-beta gate off for this org) surfaces as the error state; no FE gate,
// because the server is the source of truth.
// ---------------------------------------------------------------------------

const ConversationsList: React.FC = () => {
  const { data, isLoading, isError, refetch, isFetching } =
    useChatThreadsListQuery({ limit: CONVERSATIONS_PAGE_SIZE });

  // W8: inline-confirm delete: the row morphs in place into "Delete this
  // chat? [Cancel] [Delete]". ONE row in confirm at a time; opening B
  // collapses A. ``pendingError`` carries the inline failure copy.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const deleteMutation = useDeleteChatThreadMutation();
  const navigate = useNavigate();
  const {
    threadId: contextThreadId,
    setMessages,
    setThreadId,
  } = useChatSidebar();
  // TRAP: drive "active" off the URL, NOT the chat-sidebar context. That
  // context is a localStorage cache that survives leaving ``/portal/chat`` (so
  // the hero can rehydrate the last-seen thread), so keying on it keeps a row
  // highlighted on Dashboard / Workflow / a building detail. A row is active
  // iff the user is on ``/portal/chat?threadId=<thread.id>``.
  const location = useLocation();
  const onChatRoute = location.pathname === "/portal/chat";
  const urlThreadId =
    onChatRoute && typeof location.search === "object" && location.search
      ? (location.search as { threadId?: string }).threadId
      : undefined;
  const activeThreadId = onChatRoute ? urlThreadId : undefined;
  // ``contextThreadId`` is still read by ``handleConfirmDelete``: deleting the
  // row being viewed must wipe the context as well as the URL, and reading it
  // from context avoids a stale-URL race during the mutation.

  const handleConfirmDelete = (target: ChatThreadListItem) => {
    setPendingError(null);
    // Deleting the viewed thread resets the context AND navigates to
    // ``/portal/chat`` with no ``?threadId``: the Page's effect reads the URL,
    // so wiping the param triggers its "clear buffer back to hero" branch.
    const isActive = contextThreadId === target.id;
    deleteMutation.mutate(target.id, {
      onSuccess: () => {
        setConfirmingId(null);
        if (isActive) {
          setMessages([]);
          setThreadId(undefined);
          void navigate({ to: "/portal/chat", search: {} });
        }
      },
      onError: (err) => {
        setPendingError(
          err instanceof Error ? err.message : "Couldn't delete this chat.",
        );
      },
    });
  };

  const handleCancelDelete = () => {
    setConfirmingId(null);
    setPendingError(null);
  };

  const threads = data?.results ?? [];

  if (isLoading) {
    // Subdued skeleton rows: same vertical rhythm as a real row so
    // the list doesn't reflow when threads land.
    return (
      <ul
        className="space-y-1"
        data-testid="tracker-conversations-loading"
        aria-busy="true"
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <li key={i} className="px-3 py-2">
            <div className="h-3 w-4/5 rounded bg-ink-line animate-pulse" />
            <div className="mt-1.5 h-2 w-1/3 rounded bg-ink-line/60 animate-pulse" />
          </li>
        ))}
      </ul>
    );
  }

  if (isError) {
    return (
      <div
        className="px-3 py-3 text-2xs text-ink-mute"
        data-testid="tracker-conversations-error"
        role="alert"
      >
        <p className="text-ink-mute">Couldn&rsquo;t load conversations.</p>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="mt-1.5 text-2xs font-medium text-ink-body underline underline-offset-2 hover:text-ink disabled:opacity-50"
          data-testid="tracker-conversations-retry"
        >
          {isFetching ? "Retrying…" : "Retry"}
        </button>
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <p
        className="px-3 py-2 text-2xs text-ink-mute"
        data-testid="tracker-conversations-empty"
      >
        No conversations yet — start one.
      </p>
    );
  }

  return (
    <ul className="space-y-0.5" data-testid="tracker-conversations-list">
      {threads.map((thread) => {
        const isActive = activeThreadId === thread.id;
        const isConfirming = confirmingId === thread.id;
        if (isConfirming) {
          return (
            <li key={thread.id} data-thread-id={thread.id}>
              <ChatRowDeleteConfirm
                threadId={thread.id}
                onCancel={handleCancelDelete}
                onConfirm={() => handleConfirmDelete(thread)}
                isPending={deleteMutation.isPending}
                error={pendingError}
              />
            </li>
          );
        }
        return (
          <li
            key={thread.id}
            // ``relative`` + ``group`` so the absolutely-positioned
            // delete affordance can hover-reveal on the row, not the
            // whole sidebar.
            className="relative group"
          >
            <Link
              to="/portal/chat"
              search={{ threadId: thread.id }}
              data-testid={`tracker-conversation-${thread.id}`}
              data-thread-id={thread.id}
              data-active={isActive ? "true" : undefined}
              aria-current={isActive ? "page" : undefined}
              className="block w-full rounded-md px-3 py-1.5 pr-9 text-left motion-tint hover:bg-ink-line/40"
            >
              {/* Margin-Index active state = WEIGHT, not a color block. */}
              <div
                className={
                  "text-2xs truncate leading-snug text-ink " +
                  (isActive ? "font-semibold" : "")
                }
              >
                {resolveTitle(thread)}
              </div>
              <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.06em] text-ink-faint">
                {formatRelativeChatTime(thread.lastMessageAt)}
              </div>
            </Link>
            {/* Hover-revealed delete, a typographic ✕. ``focus-within:
                opacity-100`` sits on the parent <li> so a keyboard user
                tabbing in can see it without hovering. Click enters
                inline-confirm; the row morphs in place, no modal. */}
            <button
              type="button"
              aria-label={`Delete conversation: ${resolveTitle(thread)}`}
              data-testid={`tracker-conversation-delete-${thread.id}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setPendingError(null);
                setConfirmingId(thread.id);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 font-mono text-[12px] leading-none text-ink-faint opacity-0 motion-tint hover:bg-destructive/10 hover:text-destructive focus:opacity-100 focus-visible:opacity-100 group-hover:opacity-100"
            >
              <span aria-hidden="true">✕</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

// The Chat nav link keeps the active threadId in the URL so arriving from a
// building detail resumes the conversation: the W5 effect reads !urlThreadId
// as "clear to hero".
export const ChatNavLink: React.FC = () => {
  const { threadId } = useChatSidebar();
  return (
    <Link
      to="/portal/chat"
      search={threadId ? { threadId } : {}}
      className={navRowClass}
      activeProps={{ className: "font-semibold" }}
    >
      <span>Chat</span>
      <span className={groupMetaClass}>AI</span>
    </Link>
  );
};

/** ASK CODECLEAR (AI): the existing conversations. */
export const AskCodeClearSection: React.FC<{
  activeBuildingId: string | undefined;
  hasManagedCases: boolean;
}> = ({ activeBuildingId, hasManagedCases }) => (
  <section data-testid="tracker-ask-ai-section" className="px-5 pt-5">
    <IndexGroupRule
      left={
        <>
          <h3 className={groupTitleClass}>Ask CodeClear</h3>
          <span
            data-testid="tracker-ask-ai-badge"
            className="inline-flex items-center rounded-full border border-ink-line px-1.5 py-px font-mono text-[10px] uppercase tracking-[0.08em] text-ink-mute"
          >
            AI
          </span>
        </>
      }
      right={
        <Link
          to="/portal/chat"
          className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.08em] text-ink-mute motion-tint hover:text-ink"
          data-testid="tracker-conversations-new"
        >
          + New
        </Link>
      }
    />
    {/* Cross-link → the human species. Only when the building HAS
          cases; see hasManagedCases in TrackerSidebar. */}
    {activeBuildingId && hasManagedCases && (
      <div className="mb-2 px-3">
        <Link
          to="/portal/buildings/$buildingId"
          params={{ buildingId: activeBuildingId }}
          hash="managed-cases"
          data-testid="tracker-ai-to-cases"
          className="text-2xs text-ink-mute underline underline-offset-2 motion-tint hover:text-ink"
        >
          View this building&rsquo;s cases →
        </Link>
      </div>
    )}
    <ConversationsList />
  </section>
);
